import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Plus, CheckCircle2, Circle, Clock } from "lucide-react";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, isToday, parseISO } from "date-fns";
import { nb } from "date-fns/locale";
import TaskForm from "@/components/tasks/TaskForm";
import { cn } from "@/lib/utils";

export default function MyTasks() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ["user"],
    queryFn: () => base44.auth.me(),
  });

  const { data: tasks = [] } = useQuery({
    queryKey: ["myTasks"],
    queryFn: () => base44.entities.Task.filter({ 
      assigned_to: user?.email 
    }, "-due_date"),
    enabled: !!user,
  });

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Task.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myTasks"] });
    },
  });

  const createTaskMutation = useMutation({
    mutationFn: (data) => base44.entities.Task.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myTasks"] });
      setShowTaskForm(false);
      setEditingTask(null);
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (id) => base44.entities.Task.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myTasks"] });
    },
  });

  const handleTaskSubmit = (data) => {
    if (editingTask) {
      updateTaskMutation.mutate({ id: editingTask.id, data });
    } else {
      createTaskMutation.mutate({
        ...data,
        assigned_to: user?.email,
        assigned_to_name: user?.full_name,
      });
    }
  };

  const handleTaskStatusChange = (task) => {
    const newStatus = task.status === "completed" ? "todo" : "completed";
    updateTaskMutation.mutate({
      id: task.id,
      data: { ...task, status: newStatus }
    });
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const startDay = monthStart.getDay();
  const daysFromPrevMonth = startDay === 0 ? 6 : startDay - 1;

  const getTasksForDate = (date) => {
    return tasks.filter(task => {
      if (!task.due_date) return false;
      if (task.status === "completed" || task.status === "cancelled") return false;
      return isSameDay(parseISO(task.due_date), date);
    });
  };

  const selectedDateTasks = selectedDate ? getTasksForDate(selectedDate) : [];

  const statusColors = {
    todo: "bg-slate-100 text-slate-700",
    in_progress: "bg-blue-100 text-blue-700",
    completed: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-rose-100 text-rose-700",
  };

  const priorityColors = {
    low: "border-blue-300",
    medium: "border-amber-300",
    high: "border-orange-400",
    urgent: "border-red-500",
  };

  const openTasks = tasks.filter(t => t.status !== "completed" && t.status !== "cancelled");
  const completedTasks = tasks.filter(t => t.status === "completed");

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Mine Oppgaver</h1>
            <p className="text-slate-500 mt-1">{openTasks.length} åpne oppgaver</p>
          </div>
          <Button onClick={() => setShowTaskForm(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="w-4 h-4 mr-2" /> Ny Oppgave
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <Card className="p-6 bg-white border-0 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-slate-900">
                  {format(currentMonth, "MMMM yyyy", { locale: nb })}
                </h2>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentMonth(new Date())}
                  >
                    I dag
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Weekday headers */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"].map(day => (
                  <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-2">
                {/* Empty cells for days before month starts */}
                {Array.from({ length: daysFromPrevMonth }).map((_, i) => (
                  <div key={`empty-${i}`} className="aspect-square" />
                ))}

                {/* Days in month */}
                {daysInMonth.map(day => {
                  const dayTasks = getTasksForDate(day);
                  const isSelected = selectedDate && isSameDay(day, selectedDate);
                  const today = isToday(day);

                  return (
                    <button
                      key={day.toString()}
                      onClick={() => setSelectedDate(day)}
                      className={cn(
                        "aspect-square p-2 rounded-lg border-2 transition-all hover:border-slate-300",
                        isSelected ? "border-slate-900 bg-slate-50" : "border-slate-100",
                        today && "bg-lime-100 border-lime-200"
                      )}
                    >
                      <div className="text-sm font-medium text-slate-900 mb-1">
                        {format(day, "d")}
                      </div>
                      {dayTasks.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {dayTasks.slice(0, 3).map(task => (
                            <div
                              key={task.id}
                              className={cn(
                                "w-full h-1 rounded-full",
                                task.status === "completed" ? "bg-emerald-400" : 
                                task.priority === "urgent" ? "bg-red-400" :
                                task.priority === "high" ? "bg-orange-400" :
                                "bg-slate-300"
                              )}
                            />
                          ))}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Selected Date Details / Task List */}
          <div className="space-y-6">
            {selectedDate ? (
              <Card className="p-6 bg-white border-0 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">
                    {format(selectedDate, "d. MMMM yyyy", { locale: nb })}
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingTask({
                        due_date: format(selectedDate, "yyyy-MM-dd")
                      });
                      setShowTaskForm(true);
                    }}
                  >
                    <Plus className="w-3 h-3 mr-1" /> Legg til
                  </Button>
                </div>

                {selectedDateTasks.length > 0 ? (
                  <div className="space-y-2">
                    {selectedDateTasks.map(task => (
                      <div
                        key={task.id}
                        className={cn(
                          "p-3 rounded-lg border-l-4 bg-slate-50 cursor-pointer hover:bg-slate-100",
                          priorityColors[task.priority]
                        )}
                        onClick={() => {
                          setEditingTask(task);
                          setShowTaskForm(true);
                        }}
                      >
                        <div className="flex items-start gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTaskStatusChange(task);
                            }}
                            className="mt-0.5"
                          >
                            {task.status === "completed" ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <Circle className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <p className={cn(
                              "text-sm font-medium text-slate-900",
                              task.status === "completed" && "line-through text-slate-500"
                            )}>
                              {task.title}
                            </p>
                            {task.project_name && (
                              <p className="text-xs text-slate-500 mt-1">{task.project_name}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-8">Ingen oppgaver denne dagen</p>
                )}
              </Card>
            ) : (
              <Card className="p-6 bg-white border-0 shadow-sm">
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">Velg en dato for å se oppgaver</p>
                </div>
              </Card>
            )}

            {/* Quick Stats */}
            <Card className="p-6 bg-white border-0 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4">Oversikt</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Åpne</span>
                  <Badge variant="secondary">{openTasks.length}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Fullførte</span>
                  <Badge className="bg-emerald-100 text-emerald-700 border-0">{completedTasks.length}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Forfaller i dag</span>
                  <Badge className="bg-amber-100 text-amber-700 border-0">
                    {tasks.filter(t => t.due_date && isSameDay(parseISO(t.due_date), new Date()) && t.status !== "completed").length}
                  </Badge>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* All Tasks List */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Alle Oppgaver</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {tasks.length > 0 ? tasks.map(task => (
              <Card
                key={task.id}
                className={cn(
                  "p-4 border-l-4 hover:shadow-md transition-all cursor-pointer",
                  priorityColors[task.priority]
                )}
                onClick={() => {
                  setEditingTask(task);
                  setShowTaskForm(true);
                }}
              >
                <div className="flex items-start gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTaskStatusChange(task);
                    }}
                    className="mt-1"
                  >
                    {task.status === "completed" ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <Circle className="w-5 h-5 text-slate-400" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className={cn(
                        "font-semibold text-slate-900",
                        task.status === "completed" && "line-through text-slate-500"
                      )}>
                        {task.title}
                      </h3>
                      <Badge className={cn("border-0 shrink-0", statusColors[task.status])}>
                        {task.status === "todo" ? "Å gjøre" :
                         task.status === "in_progress" ? "Pågår" :
                         task.status === "completed" ? "Fullført" : "Kansellert"}
                      </Badge>
                    </div>
                    {task.description && (
                      <p className="text-sm text-slate-600 mb-2">{task.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                      {task.project_name && (
                        <span className="px-2 py-1 bg-slate-100 rounded">{task.project_name}</span>
                      )}
                      {task.due_date && (
                        <span className={cn(
                          "px-2 py-1 rounded",
                          new Date(task.due_date) < new Date() && task.status !== "completed"
                            ? "bg-red-100 text-red-700"
                            : "bg-slate-100"
                        )}>
                          {format(parseISO(task.due_date), "d. MMM", { locale: nb })}
                        </span>
                      )}
                      {task.priority && (
                        <span className="px-2 py-1 bg-slate-100 rounded capitalize">
                          {task.priority === "urgent" ? "Haster" :
                           task.priority === "high" ? "Høy" :
                           task.priority === "medium" ? "Medium" : "Lav"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            )) : (
              <div className="col-span-2 text-center py-12">
                <p className="text-slate-400">Ingen oppgaver ennå</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Task Form */}
      {showTaskForm && (
        <TaskForm
          task={editingTask}
          onSubmit={handleTaskSubmit}
          onClose={() => {
            setShowTaskForm(false);
            setEditingTask(null);
          }}
          isLoading={createTaskMutation.isPending || updateTaskMutation.isPending}
        />
      )}
    </div>
  );
}